/* ==========================================================
   Professional Learner — Three Adult-Level Courses
   Content from Course Sample Selection document.
   All data is static and pre-loaded.
   State: localStorage only (demo MVP).
   ========================================================== */

export const PROF_COURSES = {
  robotics: {
    id: 'robotics',
    title: 'Introduction to Consumer AI-Robotics Repair',
    subtitle: 'Read the Machine Before You Touch It',
    credential: 'Consumer Robotics Diagnostic Technician — Level 1',
    issuer: 'HDG University · Tech Tradesmith Institute',
    hours: 12,
    xpAvailable: 500,
    passScore: 0.75,
    passCount: 6, // 6 of 8
    badgeColor: '#0f2744',
    badgeAccent: '#f97316',
    badgeEmoji: '🔧',
    badgeName: 'Robotics Diagnostic Technician',
    description: "Every robot that reaches a repair bench is sending a message. Spinning in circles. Stalling after ten minutes. Driving into the same wall. This course builds your ability to read that message — to hear a customer's complaint and know which of the four subsystems to open first.",
    careerMatch: ['Technology & AI', 'Skilled Trades'],
    nodes: [
      {
        id: 'read-machine',
        title: 'Read the Machine',
        subtitle: 'NODE 1',
        kind: 'story',
        xp: 25,
        body: [
          "The unit arrived packed in a garbage bag because it was sleeting. The owner said her robot vacuum spun in circles and bumped into objects. Another shop told her to buy a new one.",
          "I looked at it for about four seconds. Drove backward. Turned. Drove backward. Turned. That read as a drive problem. One wheel pulling, one dragging.",
          "I ordered a drive motor. Three days later I fitted it and put the robot down. It moved backward, turned, moved backward, and turned.",
          "The motor was never broken.",
          "A sensor in the front bumper stayed stuck in the depressed position. Since it thought it was hitting a wall every second, the robot did the one thing it knew: move backward and turn.",
          "I didn't ask the machine what it could see until after I had already watched it for four seconds.",
        ],
        callout: {
          type: 'key',
          text: "The lesson: what you observe IS data. Every behavior is a system reporting its status. The technician's first job is not to fix — it is to read.",
        },
        quiz: [
          {
            q: "What was the actual cause of the robot's behavior?",
            options: [
              { t: 'A broken drive motor', c: false },
              { t: 'A stuck front bumper sensor', c: true },
              { t: 'Corrupted firmware', c: false },
              { t: 'A dead battery', c: false },
            ],
          },
          {
            q: 'What mistake did the technician make?',
            options: [
              { t: 'He bought the wrong part', c: false },
              { t: 'He stopped asking questions after four seconds', c: true },
              { t: 'He replaced the motor too quickly', c: false },
              { t: 'He charged too much', c: false },
            ],
          },
        ],
      },
      {
        id: 'four-subsystems',
        title: 'The Four Subsystems',
        subtitle: 'NODE 2',
        kind: 'cards',
        xp: 50,
        intro: 'Every consumer robot has four subsystems. When a robot fails, one of these four is the source.',
        cards: [
          {
            title: 'SENSORS',
            front: 'Detect the world',
            back: "A sensor converts a physical quantity into a signal. A cliff sensor measures the floor. A bumper sensor detects a collision. A camera maps the room.\n\nContamination is the most common sensor fault. Clean before you replace.",
            spec: { job: 'Detect world, report to controller', fault: 'Contamination most common — clean first' },
          },
          {
            title: 'CONTROLLER',
            front: 'The decision-maker',
            back: "The board that reads sensor inputs, runs the software, and sends commands to the actuators. If sensors and actuators both check out, the fault is almost certainly here.\n\nTwo types: hardware failure or software failure. That's your first diagnostic fork.",
            spec: { job: 'Reads, processes, commands', fault: 'Hardware vs. software is the first fork' },
          },
          {
            title: 'ACTUATORS',
            front: 'Convert power to movement',
            back: "Convert electricity into movement. Drive wheels, brush rollers, and fans are all actuators. These are the failures you can hear.\n\nAsk why it failed — not just which part failed.",
            spec: { job: 'Turn electricity into movement', fault: 'The failures you can hear — ask why' },
          },
          {
            title: 'POWER SYSTEM',
            front: 'Stores and delivers energy',
            back: "Stores energy and delivers steady voltage to everything. Short runtime lives here. Lithium-ion batteries fade after 300–500 charge cycles.\n\nCalculate cycles before ordering a replacement.",
            spec: { job: 'Stores and delivers energy', fault: 'Short runtime and voltage dips live here' },
          },
        ],
        quiz: [
          {
            q: 'A robot mower runs five minutes then stops after charging all night. Which subsystem first?',
            options: [
              { t: 'Controller — software bug', c: false },
              { t: 'Power system — runtime shortened', c: true },
              { t: 'Sensors — something stuck', c: false },
              { t: 'Actuators — blades too heavy', c: false },
            ],
          },
          {
            q: 'If sensors and actuators both test clean, where is the fault?',
            options: [
              { t: 'Always in the sensors', c: false },
              { t: 'Almost certainly in the controller', c: true },
              { t: 'In the battery', c: false },
              { t: "In the customer's usage", c: false },
            ],
          },
          {
            q: 'You hear grinding from a drive wheel. What is the right first question?',
            options: [
              { t: 'Which wheel?', c: false },
              { t: 'How old is it?', c: false },
              { t: 'Why did it fail?', c: true },
              { t: 'Does the customer want a replacement?', c: false },
            ],
          },
        ],
      },
      {
        id: 'symptoms-first',
        title: 'Symptoms First, Causes Last',
        subtitle: 'NODE 3',
        kind: 'steps',
        xp: 50,
        intro: 'Work in this order. Every step is cheaper than the one below it.',
        steps: [
          { num: 1, title: 'GATHER', text: 'Ask what changed. New room, new floor, dropped, updated. A fault that started on a specific day had a cause that day.' },
          { num: 2, title: 'POWER', text: "Does it run and for how long? If runtime was long and is now short, start here. Calculate charge cycles before ordering a battery." },
          { num: 3, title: 'ACTUATORS', text: "Command each motor and watch. A wheel that grinds under load but spins free on the bench is a transmission problem, not a dead motor." },
          { num: 4, title: 'SENSORS', text: "Change what the robot can see. Cover a cliff sensor. Press a bumper. No change means that sensor is not reaching the controller." },
          { num: 5, title: 'CONTROLLER AND FIRMWARE', text: "If everything above tests clean — reflash before you replace. Firmware is cheaper than a board." },
        ],
        warning: {
          text: "THE WRONG GUESS THAT COSTS THE MOST: A robot quits partway. You call it a tired battery. You buy the pack, install it, shutdowns keep coming. Repeated shutdowns under load are thermal — blocked airflow, dust, a failing fan. Bypass that cutoff and you risk a fire.",
        },
        quiz: [
          {
            q: 'A customer wants you to bypass the thermal shutdown. What do you do?',
            options: [
              { t: "Bypass it — they're paying", c: false },
              { t: 'Refuse. Diagnose the thermal cause instead', c: true },
              { t: 'Offer a partial bypass', c: false },
              { t: 'Replace the battery first', c: false },
            ],
          },
          {
            q: 'Which diagnostic step comes last and why?',
            options: [
              { t: 'Power', c: false },
              { t: 'Actuators', c: false },
              { t: 'Sensors', c: false },
              { t: 'Controller and firmware — if all above test clean, the fault is here', c: true },
            ],
          },
        ],
      },
      {
        id: 'spa-loop',
        title: 'The Sense-Plan-Act Loop',
        subtitle: 'NODE 4',
        kind: 'loop',
        xp: 50,
        intro: 'This cycle runs hundreds of times per second in every consumer robot. When you identify which step produced the wrong output, you have identified the subsystem with the fault.',
        loop: [
          { label: 'SENSE', text: 'Read sensor inputs', color: '#f97316', detail: 'Sensor lies: SENSE gives PLAN bad data.' },
          { label: 'PLAN', text: 'Process data, decide response', color: '#0f2744', detail: 'Software bug: PLAN makes wrong decision from correct data.' },
          { label: 'ACT', text: 'Send commands to actuators', color: '#15803d', detail: "Actuator failure: PLAN is correct but ACT can't carry it out." },
        ],
        scenarios: [
          {
            trigger: 'Robot bumps glass door repeatedly.',
            step: 'SENSE',
            text: "Glass doesn't return the light signal the way a wall does. Robot reads open space.",
          },
          {
            trigger: 'Robot receives correct data but drives into furniture anyway.',
            step: 'PLAN',
            text: 'Correct data, wrong software decision. Reflash firmware before replacing hardware.',
          },
          {
            trigger: 'Voice assistant errors every time but movement and cleaning are normal.',
            step: 'NOT A TEARDOWN',
            text: "Check the network connection. All mechanical systems work — failing feature depends on a cloud service.",
          },
        ],
        quiz: [
          {
            q: "A robot's voice assistant errors every time but moves and cleans normally. Check first:",
            options: [
              { t: 'Failed actuator', c: false },
              { t: 'Controller hardware', c: false },
              { t: 'Cliff sensors', c: false },
              { t: 'Network or cloud service connection', c: true },
            ],
          },
          {
            q: 'A stuck bumper sensor creates a fault in which step of the loop?',
            options: [
              { t: 'PLAN', c: false },
              { t: 'ACT', c: false },
              { t: 'SENSE — bad data enters the loop', c: true },
              { t: 'All steps equally', c: false },
            ],
          },
          {
            q: 'A wheel grinds under load but spins freely off the floor. This indicates:',
            options: [
              { t: 'Dead motor', c: false },
              { t: 'Sensor failure', c: false },
              { t: 'Transmission problem — motor works, drivetrain fails under load', c: true },
              { t: 'Firmware bug', c: false },
            ],
          },
        ],
      },
      {
        id: 'final-diagnostic',
        title: 'Final Diagnostic Challenge',
        subtitle: 'NODE 5 · FINAL ASSESSMENT',
        kind: 'final',
        xp: 100,
        passScore: 0.75,
        passCount: 6,
        intro: '75% or higher earns your certificate. Unlimited retakes with 24-hour cooldown.',
        quiz: [
          {
            q: 'The four subsystems in order with their jobs:',
            options: [
              { t: 'Sensors detect, Controller decides, Actuators move, Power stores and delivers', c: true },
              { t: 'Camera, Motor, Board, Battery', c: false },
              { t: 'Input, Output, Processor, Storage', c: false },
              { t: 'Front, Back, Left, Right', c: false },
            ],
          },
          {
            q: 'Robot vacuum hits the glass patio door every pass. Which subsystem?',
            options: [
              { t: 'Power', c: false },
              { t: 'Actuators', c: false },
              { t: "Sensors — glass doesn't return the light signal", c: true },
              { t: 'Controller firmware', c: false },
            ],
          },
          {
            q: 'Customer vacuums 4 times/week for 2 years. Charge cycles? Inside 300–500 fade range?',
            options: [
              { t: '208 cycles — not yet', c: false },
              { t: '416 cycles — yes, inside the range', c: true },
              { t: '520 cycles — past the range', c: false },
              { t: '312 cycles — just entering', c: false },
            ],
          },
          {
            q: 'Customer asks you to bypass thermal cutoff. You:',
            options: [
              { t: 'Bypass it — reliability matters', c: false },
              { t: 'Refuse. Diagnose the thermal cause', c: true },
              { t: 'Offer a temporary bypass', c: false },
              { t: 'Replace the battery first', c: false },
            ],
          },
          {
            q: 'Robot passes all mechanical tests but voice assistant errors on every command. Check:',
            options: [
              { t: 'Drive motors', c: false },
              { t: 'Controller board', c: false },
              { t: 'Cliff sensors', c: false },
              { t: 'Network connection', c: true },
            ],
          },
          {
            q: 'Stuck bumper sensor — which loop step fails?',
            options: [
              { t: 'PLAN', c: false },
              { t: 'ACT', c: false },
              { t: 'SENSE', c: true },
              { t: 'All steps', c: false },
            ],
          },
          {
            q: 'Which diagnostic step comes last?',
            options: [
              { t: 'Power', c: false },
              { t: 'Sensors', c: false },
              { t: 'Controller and firmware', c: true },
              { t: 'Actuators', c: false },
            ],
          },
          {
            q: 'Wheel grinds under load, spins free on bench. Ordering a replacement motor is:',
            options: [
              { t: 'Right — motor is the fault', c: false },
              { t: 'Wrong — motor works, transmission is the fault', c: true },
              { t: 'Right — replace both to be safe', c: false },
              { t: "Wrong — it's firmware", c: false },
            ],
          },
        ],
      },
    ],
  },

  agentic: {
    id: 'agentic',
    title: 'Agentic Modeling',
    subtitle: "Building AI Systems That Don't Lose Your Eleven-Year Customers",
    credential: 'AI Agentic Systems Operations Certificate',
    issuer: 'HDG University · Tech Tradesmith Institute',
    hours: 10,
    xpAvailable: 500,
    passScore: 0.75,
    passCount: 6,
    badgeColor: '#0f2744',
    badgeAccent: '#f59e0b',
    badgeEmoji: '🛡️',
    badgeName: 'AI Systems Operator',
    description: "In August, a customer of eleven years was lost. Not by a bug — by a system that did exactly what it was told. This course teaches the difference between a workflow and an agent, how much autonomy to give at each stage, and which actions always require a human in the loop.",
    careerMatch: ['Technology & AI', 'Business & Finance'],
    nodes: [
      {
        id: 'eleven-year',
        title: 'The Eleven-Year Customer',
        subtitle: 'NODE 1',
        kind: 'story',
        xp: 25,
        body: [
          "It was August in a dispatch office and the air conditioner couldn't keep up. Customer records were a mess. I built an agentic tool to merge duplicates.",
          "I gave it database access, told it to find duplicates, gave it permission to delete, and set it to work.",
          "The agent found two accounts with the same last name on the same street. They were different people — a father and son, both our customers. It merged their records and erased eleven years of service history: every furnace, every water heater, every warranty.",
          "I spent Saturday trying to recover backups. Monday I told the customer we had deleted his warranty records. His warranty was now void. We lost a client of eleven years.",
          "The system did exactly what I told it to do. Nothing went wrong with the software. I never asked: how much should it be allowed to do before someone checked?",
        ],
        callout: null,
        cards: [
          {
            title: 'WORKFLOW',
            body: "A fixed path. A person decides the steps. The AI fills them in. Example: an intake form that reads itself and routes the result. The AI does not choose the next step — you do.",
          },
          {
            title: 'AGENT',
            body: "A system that picks its own next step toward a goal you gave it. Example: 'Find out why this invoice bounced' — with no script for how. The agent decides. That is both its power and its risk.",
          },
          {
            title: 'CONSEQUENTIAL ACTION',
            body: "An action carrying legal, financial, safety, security, privacy, or reputational impact. Example: deleting a customer record. Always requires a human — no matter how well the system has been running.",
          },
        ],
        quiz: [
          {
            q: 'Key difference between a workflow and an agent:',
            options: [
              { t: 'Agents are faster', c: false },
              { t: 'Workflow follows a fixed path set by a person. Agent picks its own next step', c: true },
              { t: 'Agents always need human approval', c: false },
              { t: "Workflows use AI, agents don't", c: false },
            ],
          },
          {
            q: 'Why was the eleven-year customer lost?',
            options: [
              { t: 'A software bug', c: false },
              { t: 'Agent had delete permission with no human checkpoint required', c: true },
              { t: 'The backup system failed', c: false },
              { t: 'Wes made a data entry error', c: false },
            ],
          },
        ],
      },
      {
        id: 'autonomy-tiers',
        title: 'The Four Autonomy Tiers',
        subtitle: 'NODE 2',
        kind: 'tiers',
        xp: 50,
        intro: 'Start at the bottom. Earn your way up through evidence.',
        tiers: [
          {
            num: 1,
            title: 'SHADOW MODE',
            text: 'Agent suggests. Human acts. Nothing reaches the world without a person directing it. Costs the most time. Earns the evidence you need to move up.',
            highlight: false,
          },
          {
            num: 2,
            title: 'SUPERVISED MODE',
            text: 'Agent acts. Human approves. The action is real. The person is the gate.',
            highlight: true,
            note: 'Most consequential work should live here',
          },
          {
            num: 3,
            title: 'GUIDED AUTONOMY',
            text: "Agent acts. Human monitors. No one approves each action — so your monitoring has to be real monitoring, not periodic checking and hoping.",
            highlight: false,
          },
          {
            num: 4,
            title: 'FULL AUTONOMY',
            text: "Agent acts independently. Justified by evidence, never by confidence. Reserved only for actions you can afford to get wrong.",
            highlight: false,
          },
        ],
        scenario: {
          q: 'The duplicate-merger agent could delete without asking anyone. Which tier was it operating at?',
          answer: 'Tier 4 — full autonomy. With consequential action authority from day one. That is the mistake.',
        },
        quiz: [
          {
            q: 'A new agent deploys this week. Start at:',
            options: [
              { t: 'Tier 4 — efficiency', c: false },
              { t: 'Tier 3 — guided autonomy', c: false },
              { t: 'Tier 1 — shadow mode, build evidence first', c: true },
              { t: 'Tier 2 — always enough', c: false },
            ],
          },
          {
            q: 'Supervised mode means:',
            options: [
              { t: 'Agent suggests, human acts', c: false },
              { t: 'Agent acts, human monitors', c: false },
              { t: 'Agent acts, human approves each action', c: true },
              { t: 'Agent acts independently', c: false },
            ],
          },
          {
            q: 'Full autonomy is justified by:',
            options: [
              { t: 'Confidence the system works', c: false },
              { t: "The vendor's guarantee", c: false },
              { t: 'Evidence from observed performance at lower tiers', c: true },
              { t: 'Cost savings', c: false },
            ],
          },
        ],
      },
      {
        id: 'consequential',
        title: 'Consequential Actions',
        subtitle: 'NODE 3',
        kind: 'register',
        xp: 50,
        intro: 'For every consequential action, document three things before deployment.',
        register: [
          { action: 'Delete customer record', checkpoint: 'Human review first', approver: 'Database manager', escalation: 'Legal/Compliance' },
          { action: 'Modify billing', checkpoint: 'Human approval', approver: 'Billing supervisor', escalation: 'Finance director' },
          { action: 'Send external comms', checkpoint: 'Human review first', approver: 'Account manager', escalation: 'Senior leadership' },
          { action: 'Access health data', checkpoint: 'Consent + approval', approver: 'Privacy officer', escalation: 'Legal' },
        ],
        principle: {
          title: 'MINIMUM PRIVILEGE IS THE DEFAULT.',
          text: "Give an agent the smallest set of permissions it needs to do the job. Expanding authority later requires evidence — not confidence.",
        },
        promoGate: {
          title: 'PROMOTION GATE',
          text: "'30 days of shadow mode. 847 suggested actions. 99.3% confirmed correct by human review. We have evidence to move to supervised mode.' That is a promotion gate. Confidence is not.",
        },
        quiz: [
          {
            q: 'Which is a consequential action?',
            options: [
              { t: 'Displaying search results', c: false },
              { t: 'Sending a reminder email', c: false },
              { t: 'Deleting a financial record', c: true },
              { t: 'Sorting a list alphabetically', c: false },
            ],
          },
          {
            q: 'Minimum privilege means:',
            options: [
              { t: 'Give the agent only what it needs — no more', c: true },
              { t: 'Give full access to save setup time', c: false },
              { t: 'Limit to read-only always', c: false },
              { t: 'Remove permissions after each session', c: false },
            ],
          },
          {
            q: 'A promotion gate is:',
            options: [
              { t: 'A firewall', c: false },
              { t: 'Evidence from observed performance justifying expanded autonomy', c: true },
              { t: 'An approval form', c: false },
              { t: 'A test environment', c: false },
            ],
          },
        ],
      },
      {
        id: 'responsible-build',
        title: 'Building Responsible AI Systems',
        subtitle: 'NODE 4',
        kind: 'checklist',
        xp: 50,
        intro: 'Run this checklist before any agent with consequential action authority goes to production.',
        items: [
          'Map autonomy tiers before writing any code',
          'Set minimum privilege as the default',
          'Build a consequential action register with named approvers and escalation paths',
          'Start at shadow or supervised mode',
          'Define promotion gates — evidence, not confidence',
          'Run canary testing before full deployment',
          'Back up all data before granting delete permissions',
          'Monitor at guided autonomy — actually monitor',
        ],
        quiz: [
          {
            q: 'When do you back up data before an agent with delete permissions runs?',
            options: [
              { t: 'After successful testing', c: false },
              { t: 'Always — before it runs the first time', c: true },
              { t: 'Only for large databases', c: false },
              { t: "IT's responsibility", c: false },
            ],
          },
          {
            q: 'Canary testing means:',
            options: [
              { t: 'Testing on a small real subset before full deployment', c: true },
              { t: 'Running in a simulation', c: false },
              { t: 'Testing only on test data', c: false },
              { t: 'Getting feedback after deployment', c: false },
            ],
          },
        ],
      },
      {
        id: 'final-autonomy',
        title: 'Final Autonomy Assessment',
        subtitle: 'NODE 5 · FINAL ASSESSMENT',
        kind: 'final',
        xp: 100,
        passScore: 0.75,
        passCount: 6,
        intro: '75% or higher earns your certificate.',
        quiz: [
          {
            q: 'Workflow vs. agent — key difference:',
            options: [
              { t: 'Agents are faster', c: false },
              { t: 'Workflow: fixed path set by human. Agent: picks its own next step', c: true },
              { t: 'Agents always need approval', c: false },
              { t: "Workflows use AI, agents don't", c: false },
            ],
          },
          {
            q: 'The duplicate-merger was at which tier?',
            options: [
              { t: 'Shadow mode', c: false },
              { t: 'Supervised mode', c: false },
              { t: 'Guided autonomy', c: false },
              { t: 'Full autonomy — deleted without any checkpoint', c: true },
            ],
          },
          {
            q: 'Consequential action definition:',
            options: [
              { t: 'Any action over 10 seconds', c: false },
              { t: 'Any action with legal, financial, safety, security, privacy, or reputational impact', c: true },
              { t: 'Any irreversible action', c: false },
              { t: 'Any action involving customer data', c: false },
            ],
          },
          {
            q: 'New invoice agent deploys Monday. Start at:',
            options: [
              { t: 'Guided autonomy', c: false },
              { t: 'Full autonomy', c: false },
              { t: 'Shadow mode — build evidence first', c: true },
              { t: 'Supervised mode', c: false },
            ],
          },
          {
            q: 'A promotion gate is:',
            options: [
              { t: 'A security checkpoint', c: false },
              { t: 'An approval form', c: false },
              { t: 'Evidence from observed performance justifying higher autonomy', c: true },
              { t: 'A test environment', c: false },
            ],
          },
          {
            q: 'Agent ran shadow mode 30 days. 99.3% of suggestions confirmed correct. This justifies:',
            options: [
              { t: 'Moving to full autonomy', c: false },
              { t: 'Using this data as a promotion gate to move to supervised mode', c: true },
              { t: '30 days insufficient', c: false },
              { t: 'Removing human review', c: false },
            ],
          },
          {
            q: 'Before granting delete permissions:',
            options: [
              { t: 'Get manager approval', c: false },
              { t: 'Test in shadow mode', c: false },
              { t: 'Back up the database', c: true },
              { t: 'Limit to 100 records', c: false },
            ],
          },
          {
            q: 'Agent sends billing emails without approval. Wrong amount goes out. Root cause:',
            options: [
              { t: 'Software bug', c: false },
              { t: 'Sending billing emails was a consequential action that needed human approval — not in the register', c: true },
              { t: 'Wrong customer data', c: false },
              { t: 'Outdated template', c: false },
            ],
          },
        ],
      },
    ],
  },

  solar: {
    id: 'solar',
    title: 'Intermediate Photovoltaic Installation',
    subtitle: 'Pathway to NABCEP PVA Certification',
    credential: 'NABCEP PVA Exam Preparation Certificate',
    issuer: 'HDG University · Tech Tradesmith Institute',
    hours: 15,
    xpAvailable: 600,
    passScore: 0.75,
    passCount: 8,
    badgeColor: '#f97316',
    badgeAccent: '#f59e0b',
    badgeEmoji: '☀️',
    badgeName: 'Solar Energy Practitioner',
    description: "Solar photovoltaic installer is one of the two fastest-growing jobs in America — 42% projected growth through 2034. 4,100 openings a year. Median pay: $51,860. Top 10%: $80,150. The NABCEP PVA credential doesn't require field experience. The knowledge work starts now.",
    careerMatch: ['Solar & Clean Energy', 'Skilled Trades'],
    nabcepNote: "This course aligns with the NABCEP PVA Job Task Analysis. Earning this certificate means you have completed exam-level preparation for the NABCEP Photovoltaic Associate credential.",
    nodes: [
      {
        id: 'what-solar',
        title: 'What Solar Energy Is',
        subtitle: 'NODE 1',
        kind: 'cards',
        xp: 25,
        intro: 'Solar energy is radiation captured from sunlight and converted into usable power.',
        body: [
          "Solar PV installer is one of the two fastest-growing occupations the Bureau of Labor Statistics tracks — 42% projected growth between 2024 and 2034.",
          "About 4,100 openings a year, every year, for the next decade. Median pay as of May 2024: $51,860. The top ten percent clear $80,150.",
          "The NABCEP PVA credential doesn't require field experience. You earn it through training hours and an exam. The knowledge work starts now.",
        ],
        cards: [
          {
            title: 'ACTIVE SOLAR',
            front: 'Captured through a built device',
            back: "Three types:\n• PV panels — convert sunlight directly to electricity\n• Solar water heaters — capture heat for hot water\n• CSP — concentrate sunlight to drive a heat engine\n\nThis course focuses on PV — the technology in virtually every residential installation.",
          },
          {
            title: 'PASSIVE SOLAR',
            front: 'No powered device needed',
            back: "Building designed to collect and hold heat through thermal mass or airflow. Thick concrete walls. South-facing windows. Roof overhangs sized for summer shade.\n\nAdd a fan or pump and it becomes active or hybrid.",
          },
          {
            title: 'WHY PV EVERYWHERE',
            front: 'Why not CSP for homes?',
            back: "CSP concentrates sunlight to heat a working fluid driving a conventional heat engine — same as coal or nuclear, just with sunlight.\n\nCSP only makes economic and physical sense at utility scale. That is why residential solar is essentially always PV — not CSP.",
          },
        ],
        quiz: [
          {
            q: 'Why is residential solar essentially always PV rather than CSP?',
            options: [
              { t: 'PV is newer and more efficient', c: false },
              { t: 'CSP requires utility scale — not a rooftop technology', c: true },
              { t: 'CSP is not yet commercially available', c: false },
              { t: 'PV is always cheaper', c: false },
            ],
          },
          {
            q: 'A building with thick south-facing walls and roof overhangs to hold heat is an example of:',
            options: [
              { t: 'Active solar', c: false },
              { t: 'CSP', c: false },
              { t: 'Passive solar', c: true },
              { t: 'Grid-tied solar', c: false },
            ],
          },
        ],
      },
      {
        id: 'solar-cell',
        title: 'Inside the Solar Cell',
        subtitle: 'NODE 2',
        kind: 'loop',
        xp: 50,
        intro: "A solar cell is a layered semiconductor built from two types of doped silicon.",
        setup: [
          "N-TYPE: Doped with phosphorus — carries excess free electrons. Electron-rich.",
          "P-TYPE: Doped with boron — carries excess holes — missing electrons that act as positive charge carriers. Electron-poor.",
          "Where these layers meet, a built-in electric field forms at the junction.",
        ],
        loop: [
          {
            label: 'STEP 1',
            text: 'A sunlight photon strikes the cell and knocks an electron loose from a silicon atom, creating an electron-hole pair.',
            color: '#f97316',
            note: "A photon only frees an electron if it carries at least the bandgap energy of silicon. Photons below that threshold pass through or become heat.",
          },
          {
            label: 'STEP 2',
            text: 'The built-in electric field at the P-N junction separates the freed electron from the hole.',
            color: '#0f2744',
            note: null,
          },
          {
            label: 'STEP 3',
            text: 'The field drives the freed electron out through the external circuit as direct current (DC).',
            color: '#15803d',
            note: null,
          },
        ],
        scale: {
          title: 'Cell → Module → Array',
          text: 'One cell produces very little power. Cells wired together form a module — what most call a panel. Modules wired together form the array — the complete power-generating unit for the site.',
        },
        quiz: [
          {
            q: 'Smallest to largest — array, cell, module:',
            options: [
              { t: 'Module → cell → array', c: false },
              { t: 'Cell → module → array', c: true },
              { t: 'Array → module → cell', c: false },
              { t: 'Cell → array → module', c: false },
            ],
          },
          {
            q: 'N-type and P-type — dopant and carrier:',
            options: [
              { t: 'N-type (boron, holes) P-type (phosphorus, electrons)', c: false },
              { t: 'N-type (phosphorus, free electrons) P-type (boron, holes as positive carriers)', c: true },
              { t: 'Both layers use identical silicon', c: false },
              { t: 'N-type (carbon) P-type (silicon)', c: false },
            ],
          },
          {
            q: 'What does the electric field at the P-N junction do with the freed electron?',
            options: [
              { t: 'Absorbs it into the junction', c: false },
              { t: 'Separates electron from hole and drives it through the external circuit as DC', c: true },
              { t: 'Converts it to AC immediately', c: false },
              { t: 'Increases cell temperature', c: false },
            ],
          },
        ],
      },
      {
        id: 'pv-safety',
        title: 'Safety — A PV Circuit Is Always Live',
        subtitle: 'NODE 3 · SAFETY',
        kind: 'safety',
        xp: 75,
        banner: 'A PV CIRCUIT IS ENERGIZED WHENEVER LIGHT REACHES IT. THIS IS THE MOST IMPORTANT SAFETY RULE IN THIS COURSE.',
        body: [
          "Flip a breaker on a normal AC circuit — it goes dead. A solar module does not work that way. The moment sunlight hits the cell, the photovoltaic effect happens automatically.",
          "The module produces DC voltage whether wired into a system or sitting alone on a bench. A series string can reach several hundred volts the instant there is daylight.",
          "NEC 690.7 limits PV circuit voltage to 600 V for one- and two-family dwellings. That ceiling does not make it safe to touch.",
        ],
        rapidShutdown: {
          title: 'RAPID SHUTDOWN (NEC 690.12)',
          text: "Requires PV on buildings to include rapid shutdown — reducing conductor voltage to 30 V or less outside the array boundary within 30 seconds.\n\nWhat it does: limits voltage for firefighter safety.\nWhat it does NOT do: stop the cells from generating. The photovoltaic effect continues as long as light hits the glass.",
        },
        rule: {
          title: 'THE UNCONDITIONAL RULE',
          text: "NEVER ASSUME A PV CIRCUIT IS SAFE TO TOUCH because a disconnect is open, rapid shutdown was initiated, or the system is not commissioned yet. A qualified person must verify zero voltage with a properly rated meter before anyone touches a conductor.",
        },
        scenario: {
          q: "Crew member opened DC disconnect and initiated rapid shutdown at 1 p.m. on a clear day. Says the array is dead. Your response:",
          options: [
            { t: 'Touch it carefully', c: false },
            { t: 'Verify zero voltage first', c: true },
            { t: 'Wait 30 minutes', c: false },
          ],
          feedback: "The cells are still generating. Light on the glass means the photovoltaic effect continues regardless of any switch. Measure first.",
          xp: 20,
        },
        quiz: [
          {
            q: "DC disconnect opened, rapid shutdown initiated at noon on a clear day. The array:",
            options: [
              { t: 'Fully de-energized', c: false },
              { t: 'Still generating — light means the effect continues', c: true },
              { t: 'Goes dead in 30 seconds', c: false },
              { t: 'Produces only 12V', c: false },
            ],
          },
          {
            q: 'Before touching any PV conductor:',
            options: [
              { t: 'Supervisor approves', c: false },
              { t: 'System offline for 1 hour', c: false },
              { t: 'Qualified person verifies zero voltage with a rated meter', c: true },
              { t: 'DC disconnect must be open', c: false },
            ],
          },
          {
            q: 'NEC 690.7 residential voltage limit:',
            options: [
              { t: '120V', c: false },
              { t: '240V', c: false },
              { t: '600V', c: true },
              { t: '1,000V', c: false },
            ],
          },
        ],
      },
      {
        id: 'inverters',
        title: 'Inverters, Configurations, and Net Metering',
        subtitle: 'NODE 4',
        kind: 'cards',
        xp: 75,
        intro: 'The array produces DC. The house and grid run on AC. The inverter performs the conversion — every grid-connected PV system has one.',
        cards: [
          {
            title: 'STRING INVERTER',
            front: 'One unit for the whole array',
            back: "Listed to UL 1741, interconnects under IEEE 1547.\n\nPractical fact: the inverter is the component most likely to need replacement — well before the modules.",
          },
          {
            title: 'MICROINVERTERS',
            front: 'One per module',
            back: "Maximizes output when modules have different orientations or partial shading. More upfront cost but module-level monitoring and longer individual warranties.",
          },
          {
            title: 'MULTIMODE INVERTER',
            front: 'Also manages a battery',
            back: "Handles grid-tied operation AND battery charging/discharging. Required for hybrid systems. More complex — requires additional commissioning steps.",
          },
          {
            title: 'GRID-TIED (no battery)',
            front: 'Most common configuration',
            back: "Connected to utility. Exports surplus for credit. Cheapest and most common.\n\nCritical limit: without a battery, system shuts down in any grid outage.",
          },
          {
            title: 'OFF-GRID',
            front: 'Fully independent',
            back: "Array charges battery bank through charge controller. Survives outages. Costs more — charge controller, oversized array, often a generator, no utility export credit.",
          },
          {
            title: 'HYBRID',
            front: 'Grid-connected + battery-backed',
            back: "Earns export credit and keeps critical loads running in outages. Fastest-growing residential configuration. Cost between grid-tied and full off-grid.",
          },
        ],
        netMetering: {
          title: 'NET METERING',
          text: "Credits surplus you export at the retail rate — lowers the bill, does not eliminate it. Owner still draws from the grid at night and still pays fixed utility charges.\n\nCRITICAL: The credit rate varies by state and utility. California's Net Billing Tariff (effective April 2023) credits exports at $0.04–$0.08/kWh — far below retail. Never quote a savings figure without checking the tariff their specific utility applies.",
        },
        quiz: [
          {
            q: 'What converts PV array DC output to AC?',
            options: [
              { t: 'Charge controller', c: false },
              { t: 'Circuit breaker', c: false },
              { t: 'Inverter', c: true },
              { t: 'Disconnect switch', c: false },
            ],
          },
          {
            q: 'Reliable grid, wants cheapest option with export credit. Recommend:',
            options: [
              { t: 'Off-grid', c: false },
              { t: 'Grid-tied — no battery', c: true },
              { t: 'Hybrid', c: false },
              { t: 'CSP system', c: false },
            ],
          },
          {
            q: "Customer says net metering will eliminate their electric bill. You tell them:",
            options: [
              { t: 'Correct if system sized right', c: false },
              { t: "Net metering lowers the bill but doesn't eliminate it — they still pay fixed charges and draw from the grid at night", c: true },
              { t: 'Net metering only applies in some states', c: false },
              { t: 'Net metering has been eliminated nationally', c: false },
            ],
          },
        ],
      },
      {
        id: 'nabcep-final',
        title: 'NABCEP Final Assessment',
        subtitle: 'NODE 5 · FINAL ASSESSMENT',
        kind: 'final',
        xp: 100,
        passScore: 0.75,
        passCount: 8,
        intro: '10 questions aligned with the NABCEP PVA Job Task Analysis. 75% to earn your certificate.',
        quiz: [
          {
            q: 'Two doped silicon layers — dopant and carrier:',
            options: [
              { t: 'N-type (phosphorus, free electrons) P-type (boron, holes as positive carriers)', c: true },
              { t: 'N-type (boron, holes) P-type (phosphorus)', c: false },
              { t: 'Top layer positive, bottom negative', c: false },
              { t: 'Active layer and passive layer', c: false },
            ],
          },
          {
            q: 'Correct order smallest to largest:',
            options: [
              { t: 'Module → cell → array', c: false },
              { t: 'Cell → module → array', c: true },
              { t: 'Array → module → cell', c: false },
              { t: 'All the same', c: false },
            ],
          },
          {
            q: 'Photon strikes cell — three steps to DC:',
            options: [
              { t: 'Heats junction → melts → current flows', c: false },
              { t: 'Knocks electron loose → P-N field separates electron from hole → drives electron through external circuit as DC', c: true },
              { t: 'Charges battery → discharges → current flows', c: false },
              { t: 'Activates inverter → converts → flows', c: false },
            ],
          },
          {
            q: 'Why does the inverter exist?',
            options: [
              { t: 'Protects modules from surges', c: false },
              { t: 'Array produces DC, house requires AC, inverter converts between them', c: true },
              { t: 'Stores excess energy for night', c: false },
              { t: 'Regulates utility voltage', c: false },
            ],
          },
          {
            q: 'Why is residential solar essentially always PV rather than CSP?',
            options: [
              { t: 'PV is newer', c: false },
              { t: 'PV is always cheaper', c: false },
              { t: 'CSP requires utility scale — not a rooftop technology', c: true },
              { t: 'CSP not approved for residential', c: false },
            ],
          },
          {
            q: 'Disconnect opened, rapid shutdown initiated, 1 p.m. clear day — what is true?',
            options: [
              { t: 'System fully de-energized', c: false },
              { t: 'Cells still generating — must verify zero voltage with rated meter before touching any conductor', c: true },
              { t: 'Wait 30 minutes to discharge', c: false },
              { t: 'De-energized inside boundary only', c: false },
            ],
          },
          {
            q: 'Home A: reliable grid. Home B: weekly multi-hour outages. Recommend:',
            options: [
              { t: 'Both: grid-tied', c: false },
              { t: 'Home A: grid-tied. Home B: hybrid or off-grid', c: true },
              { t: 'Both: hybrid', c: false },
              { t: 'Home A: off-grid. Home B: grid-tied', c: false },
            ],
          },
          {
            q: "Customer says net metering eliminates their bill. Correct the misunderstanding:",
            options: [
              { t: "They're right if sized correctly", c: false },
              { t: "Net metering credits surplus, lowers the bill but does not eliminate it — verify export credit rate before quoting savings", c: true },
              { t: 'Net metering eliminated in most states', c: false },
              { t: 'Advise off-grid instead', c: false },
            ],
          },
          {
            q: 'Three limitations to raise before customer signs:',
            options: [
              { t: 'Panel color, timeline, permit cost', c: false },
              { t: 'Physical space required, weather and daylight dependence, substantial up-front cost', c: true },
              { t: 'HOA restrictions, roof age, utility approval', c: false },
              { t: 'Only if customer asks', c: false },
            ],
          },
          {
            q: "Customer says brother-in-law got 30% federal credit last year. Will they?",
            options: [
              { t: 'Yes, 30% still available', c: false },
              { t: 'Section 25D ended for expenditures after December 31, 2025. Verify current state and local incentives before quoting any number', c: true },
              { t: 'Credit reduced to 15%', c: false },
              { t: 'Yes — it will be renewed', c: false },
            ],
          },
        ],
      },
    ],
  },
};

export const PROF_COURSES_LIST = [
  PROF_COURSES.robotics,
  PROF_COURSES.agentic,
  PROF_COURSES.solar,
];

/* Career interest → recommended courses */
export const CAREER_RECOMMENDATIONS = {
  'Technology & AI':      ['robotics', 'agentic'],
  'Solar & Clean Energy': ['solar', 'robotics'],
  'Skilled Trades':       ['solar', 'robotics'],
  'Healthcare':           ['agentic'],
  'Business & Finance':   ['agentic'],
};

/* Facilities list for intake */
export const FACILITIES = [
  'North Texas Community Education Center',
  'Dallas County Jail',
  'Tarrant County Corrections Center',
  'Collin County Detention Center',
  'Other Facility',
];

/* Employer matches per credential */
export const PROF_EMPLOYERS = {
  robotics: [
    { name: 'TechForce Solutions', title: 'Consumer Electronics Repair Technician', salary: '$42,000–$58,000', location: 'Plano, TX', openings: 34 },
    { name: 'Urban Tech Services', title: 'Robotics Service Specialist', salary: '$44,000–$60,000', location: 'Dallas, TX', openings: 19 },
  ],
  agentic: [
    { name: 'North Texas Cloud Collective', title: 'AI Operations Associate', salary: '$52,000–$68,000', location: 'Irving, TX', openings: 63 },
    { name: 'Lone Star Data Partners', title: 'AI Systems Coordinator', salary: '$48,000–$64,000', location: 'Dallas, TX', openings: 41 },
  ],
  solar: [
    { name: 'Lone Star Energy Partners', title: 'PV Installation Apprentice', salary: '$38,000–$52,000', location: 'Denton, TX', openings: 127 },
    { name: 'SunPath Installations', title: 'Solar PV Technician', salary: '$42,000–$56,000', location: 'Fort Worth, TX', openings: 88 },
  ],
};
